import { Avatar, Box, IconButton, Stack, useTheme } from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { DottedBox, FoodsDeleteButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { SupplierFormAppearance } from "../../SupplierForm.types";

export type UserInterface = {
    "@id": IRI;
    firstname: string;
    lastname: string;
};

type DisplayUsersProps = {
    appearance?: SupplierFormAppearance;
    users: UserInterface[];
    onDelete?: (userId: string) => void;
};
export const DisplayUsers = (props: DisplayUsersProps) => {
    const { appearance = "legacy", users, onDelete } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    if (users.length === 0) {
        if (appearance === "legacy") {
            return <Typography variant="body">{t("aucun_utilisateur_ajoute")}</Typography>;
        }

        return (
            <DottedBox
                variant="themed"
                BoxPropsSxOverride={{
                    alignItems: "center",
                    backgroundColor: theme.palette.primary[50],
                    borderRadius: theme.spacing(1),
                    boxSizing: "border-box",
                    justifyContent: "center",
                    padding: theme.spacing(1.5),
                    width: "100%",
                }}
            >
                <Typography role="status" variant="body-medium" sx={{ color: theme.custom.grey[2000] }}>
                    {t("aucun_utilisateur_ajoute")}
                </Typography>
            </DottedBox>
        );
    }

    return (
        <Stack
            direction="row"
            sx={{
                flexWrap: "wrap",
            }}
            gap={theme.spacing(1.5)}
        >
            {users.map((user) => (
                <UserSmallCard key={user["@id"]} appearance={appearance} user={user} onDelete={onDelete} />
            ))}
        </Stack>
    );
};

type UserSmallCardProps = {
    appearance: SupplierFormAppearance;
    user: UserInterface;
    onDelete?: (userId: string) => void;
};
const UserSmallCard = (props: UserSmallCardProps) => {
    const { appearance, user, onDelete } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const fullName = `${user.firstname} ${user.lastname}`;
    const initials = `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`.toLocaleUpperCase();

    if (appearance === "legacy") {
        return (
            <Box
                component="article"
                sx={{
                    alignItems: "center",
                    border: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                    borderRadius: theme.spacing(1),
                    display: "flex",
                    gap: theme.spacing(0.625),
                    justifyContent: "center",
                    padding: theme.spacing(1),
                    width: "fit-content",
                }}
            >
                <Avatar alt={fullName} variant="small" />
                <Typography variant="body-bold">{user.firstname}</Typography>
                <Typography variant="body-bold">{user.lastname}</Typography>
                {onDelete !== undefined && (
                    <IconButton
                        aria-label={`${t("common.actions.delete", "Delete")} ${fullName}`}
                        size="small"
                        onClick={() => onDelete(user["@id"])}
                    >
                        <DeleteOutline />
                    </IconButton>
                )}
            </Box>
        );
    }

    return (
        <Stack
            component="article"
            direction="row"
            alignItems="center"
            sx={{
                border: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                borderRadius: theme.spacing(1),
                padding: theme.spacing(1),
                width: "fit-content",
            }}
            gap={theme.spacing(1)}
        >
            <Box
                aria-hidden
                sx={{
                    alignItems: "center",
                    backgroundColor: theme.custom.grey[300],
                    borderRadius: "50%",
                    display: "inline-flex",
                    flexShrink: 0,
                    height: theme.spacing(4),
                    justifyContent: "center",
                    width: theme.spacing(4),
                }}
            >
                <Typography variant="small-semibold" sx={{ color: theme.custom.grey[2500] }}>
                    {initials}
                </Typography>
            </Box>
            <Typography variant="body-semibold">{fullName}</Typography>
            {onDelete !== undefined && (
                <FoodsDeleteButton
                    ariaLabel={`${t("common.actions.delete", "Delete")} ${fullName}`}
                    onClick={() => onDelete(user["@id"])}
                    buttonProps={{ type: "button" }}
                    tooltip={{ text: t("common.actions.delete", "Delete") }}
                />
            )}
        </Stack>
    );
};
